import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Calendar,
  ChevronDown,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { AuthService } from "@/integrations/api/services/authService";
import { PaidPendingService } from "@/integrations/api/services/paidPendingService";
import { ChargesWaiverService } from "@/integrations/api/services/chargesWaiverService";
import CollectionExportDialog from "@/components/CollectionExportDialog";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BRAND_GREEN = "#22B55E";

function PendingCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gray-100 px-1 text-[11px] font-semibold tabular-nums text-gray-700">
      {count > 99 ? "99+" : count}
    </span>
  );
}

const AppHeader = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [waiverPendingCount, setWaiverPendingCount] = useState(0);

  const userDisplayName = useMemo(() => {
    if (!user) return "";
    return user.user_name || user.name || "User";
  }, [user]);

  const userRole = useMemo(() => {
    if (!user) return "";
    return user.role || AuthService.getCurrentUserRole() || "User";
  }, [user]);

  const isAdmin = AuthService.isAdmin();
  const canReviewWaiver = AuthService.canReviewChargesWaiver();
  const canViewTransactions =
    isAdmin || canReviewWaiver || AuthService.isBE() || AuthService.isTL() || AuthService.isRM();
  const transactionsLabel = isAdmin || canReviewWaiver ? "Operations" : "Transactions";
  const operationsCount = pendingCount + waiverPendingCount;

  const dashboardActive = pathname === "/";
  const attendanceActive = pathname.startsWith("/attendance");
  const operationsActive = pathname.startsWith("/paid-pending");
  const analyticsActive = pathname.startsWith("/analytics");

  useEffect(() => {
    if (!canViewTransactions) return;
    let cancelled = false;
    PaidPendingService.getPaidPendingApplications({
      approval_status: "pending",
      limit: 1,
      offset: 0,
    })
      .then((data) => {
        if (!cancelled) setPendingCount(Number(data.total_applications) || 0);
      })
      .catch(() => {
        if (!cancelled) setPendingCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [canViewTransactions]);

  useEffect(() => {
    if (!canReviewWaiver) return;
    let cancelled = false;
    ChargesWaiverService.list({ status: "pending", limit: 1, offset: 0 })
      .then((data) => {
        if (!cancelled) setWaiverPendingCount(Number(data.counts?.pending) || 0);
      })
      .catch(() => {
        if (!cancelled) setWaiverPendingCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [canReviewWaiver]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const desktopLink = (active: boolean) =>
    `inline-flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors ${
      active
        ? "bg-[#e8f8ef] text-gray-900"
        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
    }`;

  const bottomItem = (active: boolean) =>
    `flex min-w-0 flex-1 flex-col items-center gap-0.5 px-0.5 py-2 text-[10px] font-medium leading-tight ${
      active ? "text-[#1ea34f]" : "text-gray-500"
    }`;

  return (
    <>
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-2 px-3 sm:gap-6 sm:px-6 md:h-[4.5rem] lg:px-8">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex shrink-0 items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 sm:gap-3"
            aria-label="Go to Dashboard"
          >
            <img src="/uploads/logo.png" alt="" className="h-8 w-auto md:h-10" />
            <span className="text-left">
              <span className="block text-lg font-semibold leading-none tracking-tight text-gray-900 md:text-xl">ProsParity</span>
              <span className="mt-0.5 block text-[13px] font-medium leading-none text-gray-500 md:-mt-0.5 md:text-sm">Collection App</span>
            </span>
          </button>

          <nav className="hidden min-w-0 flex-1 md:flex min-[1100px]:justify-center" aria-label="Primary">
            <div className="flex w-full items-center gap-1 overflow-x-auto overflow-y-hidden min-[1100px]:w-auto min-[1100px]:overflow-visible [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300">
              <button type="button" className={desktopLink(dashboardActive)} onClick={() => navigate("/")}>
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </button>
              {FEATURE_FLAGS.SHOW_ATTENDANCE && (
                <button
                  type="button"
                  className={desktopLink(attendanceActive)}
                  onClick={() => navigate("/attendance")}
                >
                  <Calendar className="h-4 w-4" />
                  Attendance
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className={desktopLink(analyticsActive)}>
                    <BarChart3 className="h-4 w-4" />
                    Analytics
                    <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" sideOffset={8} className="z-[80] min-w-[12rem]">
                  <DropdownMenuItem
                    className="cursor-pointer py-2"
                    onClick={() => navigate("/analytics?view=collection")}
                  >
                    Collection Analytics
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="cursor-pointer py-2"
                    onClick={() => navigate("/analytics?view=business")}
                  >
                    Business Analytics
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <CollectionExportDialog triggerClassName={desktopLink(false)} label="Report" />
              {canViewTransactions && (
                <button
                  type="button"
                  className={desktopLink(operationsActive)}
                  onClick={() => navigate("/paid-pending")}
                >
                  <ClipboardCheck className="h-4 w-4" />
                  {transactionsLabel}
                  <PendingCount count={operationsCount} />
                </button>
              )}
            </div>
          </nav>

          <div className="ml-auto flex min-w-0 items-center justify-end gap-2 sm:gap-3">
            <div className="min-w-0 text-right">
              <div className="truncate text-xs font-semibold leading-none text-gray-900 sm:text-sm">
                {userDisplayName}
              </div>
              <div className="mt-1 truncate text-[10px] font-medium capitalize leading-none text-gray-500 sm:text-[11px]">
                {userRole}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              aria-label="Sign out"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {typeof document !== "undefined" &&
        createPortal(
      <nav
        className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white md:hidden"
        aria-label="Primary"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-stretch">
          <button type="button" className={bottomItem(dashboardActive)} onClick={() => navigate("/")}>
            <LayoutDashboard className="h-5 w-5" style={dashboardActive ? { color: BRAND_GREEN } : undefined} />
            Dashboard
          </button>
          {FEATURE_FLAGS.SHOW_ATTENDANCE && (
            <button type="button" className={bottomItem(attendanceActive)} onClick={() => navigate("/attendance")}>
              <Calendar className="h-5 w-5" style={attendanceActive ? { color: BRAND_GREEN } : undefined} />
              Attendance
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className={bottomItem(analyticsActive)}>
                <BarChart3 className="h-5 w-5" style={analyticsActive ? { color: BRAND_GREEN } : undefined} />
                Analytics
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" side="top" sideOffset={8} className="z-[80] min-w-[12rem]">
              <DropdownMenuItem className="cursor-pointer py-2" onClick={() => navigate("/analytics?view=collection")}>
                Collection Analytics
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer py-2" onClick={() => navigate("/analytics?view=business")}>
                Business Analytics
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <CollectionExportDialog triggerClassName={bottomItem(false)} iconClassName="h-5 w-5" label="Report" />
          {canViewTransactions && (
            <button type="button" className={bottomItem(operationsActive)} onClick={() => navigate("/paid-pending")}>
              <span className="relative">
                <ClipboardCheck className="h-5 w-5" style={operationsActive ? { color: BRAND_GREEN } : undefined} />
                {operationsCount > 0 && (
                  <span className="absolute -right-2 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-900 px-1 text-[9px] font-semibold leading-none text-white">
                    {operationsCount > 99 ? "99+" : operationsCount}
                  </span>
                )}
              </span>
              <span className="max-w-full truncate">{transactionsLabel}</span>
            </button>
          )}
        </div>
      </nav>,
          document.body,
        )}
    </>
  );
};

export default AppHeader;
